import { useCallback, useEffect, useState } from 'react';
import { api, describeError } from '../api';
import { CountedField, JsonEditor, Message, NAME_MAX, Pill, Section, fmtTime, useJsonText } from '../components/ui';
import { useSession } from '../session';

const API_IDS = ['openai', 'textgenerationwebui', 'kobold', 'novel', 'instruct', 'context', 'sysprompt', 'reasoning'] as const;
interface Item { id: string; name: string; updated_at: string; is_seed: boolean; source: 'platform' | 'user'; is_default?: boolean; summary?: { prompts_total: number; prompts_enabled: number; temperature: number | null; top_p: number | null; max_tokens: number | null } }
interface Prompt { identifier: string; name?: string; role?: string; content?: string; marker?: boolean; system_prompt?: boolean }
interface OrderItem { identifier: string; enabled: boolean }
interface Order { character_id: number; order: OrderItem[] }

export function PresetsPage() {
  const { env, me } = useSession();
  const [apiId, setApiId] = useState<(typeof API_IDS)[number]>('openai');
  const [items, setItems] = useState<Item[]>([]);
  const [selected, setSelected] = useState<{ id: string; name: string; json: Record<string, unknown>; seed: boolean } | null>(null);
  const [bundle, setBundle] = useState<{ prompts: Prompt[]; prompt_order: Order[] } | null>(null);
  const [newName, setNewName] = useState('');
  const [temp, setTemp] = useState('');
  const [topP, setTopP] = useState('');
  const [maxTok, setMaxTok] = useState('');
  const [newPrompt, setNewPrompt] = useState({ name: 'Style', content: 'Be brief' });
  const editor = useJsonText({});
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!me) return;
    try { setItems((await api<Item[]>(`/api/presets/${apiId}`)).data); } catch (e) { setErr(describeError(e)); }
  }, [me, apiId]);
  useEffect(() => { void load(); setSelected(null); setBundle(null); }, [load, env]);

  const run = async (fn: () => Promise<string | void>) => {
    setErr(null); setMsg(null);
    try { const m = await fn(); if (m) setMsg(m); await load(); } catch (e) { setErr(describeError(e)); }
  };
  const loadPrompts = async (id: string) => {
    if (apiId !== 'openai') { setBundle(null); return; }
    try { setBundle((await api<{ prompts: Prompt[]; prompt_order: Order[] }>(`/api/presets/openai/${encodeURIComponent(id)}/prompts`)).data); } catch (e) { setErr(describeError(e)); }
  };
  const open = async (id: string, name: string) => {
    setErr(null);
    try {
      const { data, headers } = await api<Record<string, unknown>>(`/api/presets/${apiId}/${encodeURIComponent(id)}`);
      setSelected({ id, name, json: data, seed: headers.get('X-Preset-Seed') === '1' });
      editor.reset(data);
      setTemp(data.temperature != null ? String(data.temperature) : '');
      setTopP(data.top_p != null ? String(data.top_p) : '');
      setMaxTok(data.openai_max_tokens != null ? String(data.openai_max_tokens) : '');
      await loadPrompts(id);
    } catch (e) { setErr(describeError(e)); }
  };
  const save = () => {
    if (!selected?.id) return;
    const v = editor.parse();
    if (v === undefined) return;
    void run(async () => {
      const { data } = await api<{ id: string; name: string }>(`/api/presets/${apiId}/${encodeURIComponent(selected.id)}`, { method: 'PUT', body: v });
      await open(data.id, data.name);
      return `已按 id 更新 ${data.name}`;
    });
  };
  const restore = (id: string, name: string) => run(async () => { await api(`/api/presets/${apiId}/${encodeURIComponent(id)}/restore`, { method: 'POST' }); await open(id, name); return `已恢复种子 ${name}`; });
  const remove = (id: string) => run(async () => { await api(`/api/presets/${apiId}/${encodeURIComponent(id)}`, { method: 'DELETE' }); setSelected(null); setBundle(null); return '已删除'; });
  const duplicate = (id: string, name: string) => {
    const numbered = /^(.*?)(\d+)$/.exec(name);
    const suggested = numbered?.[2] && Number.isSafeInteger(Number(numbered[2])) ? `${numbered[1]}${Number(numbered[2]) + 1}` : `${name}1`;
    const target = prompt('副本名称（可空 = 末尾数字 +1）', suggested);
    if (target === null) return;
    void run(async () => {
      const { data } = await api<{ id: string; name: string; source: string }>(`/api/presets/${apiId}/${encodeURIComponent(id)}/duplicate`, { method: 'POST', ...(target.trim() ? { body: { name: target.trim() } } : {}) });
      await open(data.id, data.name);
      return `已复制为 ${data.name}（source=${data.source}）`;
    });
  };
  const createFrom = () => {
    if (!newName.trim()) return;
    void run(async () => {
      const { data } = await api<Record<string, unknown>>(`/api/presets/${apiId}`, { method: 'POST', body: { name: newName.trim() } });
      await open(String(data.id), newName.trim());
      setNewName('');
      return `已创建 ${newName.trim()}（${Object.keys(data).length} 键）`;
    });
  };
  const patchSampling = (id: string) => run(async () => {
    const body: Record<string, number> = {};
    if (temp !== '') body.temperature = Number(temp);
    if (topP !== '') body.top_p = Number(topP);
    if (maxTok !== '') body.openai_max_tokens = Number(maxTok);
    const { data } = await api<{ id: string; name: string }>(`/api/presets/${apiId}/${encodeURIComponent(id)}`, { method: 'PATCH', body });
    await open(data.id, data.name);
    return `已另存采样到 ${data.name}`;
  });
  const defaultOrder = (): OrderItem[] => {
    const orders = bundle?.prompt_order ?? [];
    return (orders.find((o) => o.character_id === 100001) ?? orders[0])?.order ?? [];
  };
  const enabledOf = (id: string) => defaultOrder().find((o) => o.identifier === id)?.enabled === true;
  const putOrder = (id: string, next: OrderItem[]) => run(async () => {
    await api(`/api/presets/openai/${encodeURIComponent(id)}/prompt-order`, { method: 'PUT', body: { order: next } });
    await loadPrompts(id);
    return '已更新 prompt-order';
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
      <Section title="预设">
        {!me && <Message text="请先登录" />}
        <div className="flex flex-wrap gap-1">
          {API_IDS.map((id) => <button key={id} onClick={() => setApiId(id)} className={`rounded-lg px-2 py-1 text-xs ${apiId === id ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-zinc-100'}`}>{id}</button>)}
        </div>
        <div className="flex flex-wrap gap-2">
          <CountedField limit={NAME_MAX} placeholder="新预设名" value={newName} onChange={setNewName} />
          <button className="btn" onClick={createFrom}>POST 新建</button>
        </div>
        <Message text={err} kind="error" />
        <Message text={msg} kind="ok" />
        <ul className="max-h-[32rem] divide-y divide-zinc-800 overflow-auto">
          {items.map((p) => (
            <li key={p.id} className={`flex cursor-pointer items-center justify-between py-1.5 text-sm ${selected?.id === p.id ? 'bg-zinc-800/40' : ''}`} onClick={() => void open(p.id, p.name)}>
              <span>
                {p.name} <Pill tone={p.source === 'platform' ? 'indigo' : 'emerald'}>{p.source}</Pill>
                {p.is_default && <Pill tone="amber">default</Pill>}
                {p.summary && <span className="ml-2 text-[11px] text-zinc-500" title="summary">{p.summary.prompts_enabled}/{p.summary.prompts_total} 段 · temp {p.summary.temperature ?? '—'} · top_p {p.summary.top_p ?? '—'} · max {p.summary.max_tokens ?? '—'}</span>}
              </span>
              <span className="text-[11px] text-zinc-500">{fmtTime(p.updated_at)}</span>
            </li>
          ))}
        </ul>
      </Section>
      <Section
        title={selected ? `${apiId}/${selected.name}` : '编辑器'}
        actions={selected && (
          <>
            {!selected.seed && <button className="btn" onClick={() => save()}>PUT 按 id 保存</button>}
            <button className="btn-ghost" onClick={() => duplicate(selected.id, selected.name)}>复制</button>
            <button className="btn-ghost" onClick={() => void restore(selected.id, selected.name)}>恢复默认</button>
            {!selected.seed && <button className="btn-ghost text-rose-300" onClick={() => void remove(selected.id)}>删除</button>}
          </>
        )}
      >
        {selected ? (
          <div className="space-y-3">
            {selected.seed && <div className="text-xs text-amber-200/90">系统预设不可改内容、不可删除、不可改条目勾选。采样 PATCH 会另存一份，改内容请先复制。</div>}
            <div className="grid gap-2 text-xs sm:grid-cols-4">
              <label className="space-y-0.5"><span className="text-zinc-400">temperature</span><input className="input py-1" value={temp} onChange={(e) => setTemp(e.target.value)} /></label>
              <label className="space-y-0.5"><span className="text-zinc-400">top_p</span><input className="input py-1" value={topP} onChange={(e) => setTopP(e.target.value)} /></label>
              <label className="space-y-0.5"><span className="text-zinc-400">openai_max_tokens</span><input className="input py-1" value={maxTok} onChange={(e) => setMaxTok(e.target.value)} /></label>
              <div className="flex items-end"><button className="btn" onClick={() => void patchSampling(selected.id)}>PATCH 采样</button></div>
            </div>
            {apiId === 'openai' && bundle && (
              <div className="space-y-2 rounded border border-zinc-800 p-2">
                <div className="text-xs text-zinc-400">PromptManager（GET/POST/PATCH/DELETE …/prompts · PUT prompt-order）</div>
                <ul className="max-h-56 space-y-1 overflow-auto text-xs">
                  {bundle.prompts.map((p) => (
                    <li key={p.identifier} className="flex flex-wrap items-center gap-2 rounded bg-zinc-900/60 px-2 py-1">
                      <input type="checkbox" disabled={selected.seed} checked={enabledOf(p.identifier)} onChange={(e) => {
                        const cur = defaultOrder();
                        const next = cur.some((o) => o.identifier === p.identifier) ? cur.map((o) => o.identifier === p.identifier ? { ...o, enabled: e.target.checked } : o) : [...cur, { identifier: p.identifier, enabled: e.target.checked }];
                        void putOrder(selected.id, next);
                      }} />
                      <span className="font-mono text-zinc-300">{p.identifier}</span>
                      {p.marker && <Pill>marker</Pill>}
                      <span className="text-zinc-500">{p.name}</span>
                      {!selected.seed && <button className="btn-ghost py-0 text-[11px]" onClick={() => {
                        const content = prompt('content', p.content ?? '');
                        if (content === null) return;
                        void run(async () => { await api(`/api/presets/openai/${encodeURIComponent(selected.id)}/prompts/${encodeURIComponent(p.identifier)}`, { method: 'PATCH', body: { content } }); await loadPrompts(selected.id); return `已改 ${p.identifier}`; });
                      }}>改 content</button>}
                      {!selected.seed && <button className="btn-ghost py-0 text-[11px] text-rose-300" onClick={() => void run(async () => { await api(`/api/presets/openai/${encodeURIComponent(selected.id)}/prompts/${encodeURIComponent(p.identifier)}`, { method: 'DELETE' }); await loadPrompts(selected.id); return `已删 ${p.identifier}`; })}>删除</button>}
                    </li>
                  ))}
                </ul>
                {!selected.seed && <div className="flex flex-wrap gap-2">
                  <CountedField limit={NAME_MAX} className="input py-1" placeholder="新条目 name" value={newPrompt.name} onChange={(v) => setNewPrompt({ ...newPrompt, name: v })} />
                  <CountedField className="input flex-1 py-1" placeholder="content" value={newPrompt.content} onChange={(v) => setNewPrompt({ ...newPrompt, content: v })} />
                  <button className="btn" onClick={() => void run(async () => { const { data } = await api<Prompt>(`/api/presets/openai/${encodeURIComponent(selected.id)}/prompts`, { method: 'POST', body: { name: newPrompt.name, role: 'system', content: newPrompt.content } }); await loadPrompts(selected.id); return `已追加 ${data.identifier}`; })}>POST 条目</button>
                  <button className="btn-ghost text-rose-300" title="marker 槽位应 400 LIBRARY_PRESET_PROMPT_PROTECTED" onClick={() => void run(async () => { await api(`/api/presets/openai/${encodeURIComponent(selected.id)}/prompts/chatHistory`, { method: 'DELETE' }); return 'unexpected'; })}>试删 chatHistory</button>
                </div>}
              </div>
            )}
            <JsonEditor value={editor.text} onChange={editor.setText} error={editor.error} rows={18} />
          </div>
        ) : <div className="text-xs text-zinc-500">选择一个预设。单条地址是 /api/presets/&#123;api_id&#125;/&#123;id&#125;，不要用 name。新建走 POST /api/presets/&#123;api_id&#125;。</div>}
      </Section>
    </div>
  );
}
